import { useMemo } from 'react';
import { useFeatureFlags, type FeatureFlags } from './useFeatureFlags';
import { NavItem, NavArray } from '../../routing/nav/DefaultNav';
import { AccountWithType, AccountWithUserType } from '../../util/AccountUtils';
import { SubscriptionType } from '../../components/constants/securspace-constants';
import { NavMenuItemType } from "../../views/nav-menu/NavMenuSubmenuListItem";
import {
  //note: comment out any navs that are not currently in use in FEATURE_FLAG_NAV_CONFIG
  //LandingNav,
  //LandingNavHome,
  AccountNav,
  //AdminNav,
  //BuyerNav,
  GateManagementNav,
  //SupplierNav,
  //MarketplaceOnlyGmsUserNav
} from '../../routing/nav/navs';

/**
 * Configuration for feature-flagged navigation items.
 * Maps LaunchDarkly flag names to the nav item paths they control.
 *
 * To add a new feature-flagged nav item:
 * 1. Add the flag name to FeatureFlags interface
 * 2. Add an entry to FEATURE_FLAG_NAV_CONFIG
 * 3. Optionally add subscriptionTypes to restrict by user subscription
 */
interface NavFlagConfig {
  parentPath: string;
  navPath: string;
  subscriptionTypes?: string[];
}

//note, they key must match the name of the featureFlag
const FEATURE_FLAG_NAV_CONFIG: Partial<Record<keyof FeatureFlags, NavFlagConfig>> = {
  supplierApiIntegration: {
    parentPath: 'account',
    navPath: AccountNav.API_INTEGRATION.path,
    subscriptionTypes: [SubscriptionType.GMS_PRO],
  },
  supplierPendingInventoryActivity: {
    parentPath: 'gate-management',
    navPath: GateManagementNav.SUPPLIER_PENDING_INVENTORY_ACTIVITY.path,
    subscriptionTypes: [SubscriptionType.GMS_PRO],
  },//*/
  // Add more feature-flagged nav items here:
  // exampleFeatureFlag: {
  //   parentPath: 'account',
  //   navPath: AccountNav.SOME_FEATURE.path,
  //   subscriptionTypes: [SubscriptionType.GMS_PRO, SubscriptionType.GMS_LITE], // optional
  // },
};

interface NavItemWithSubmenu {
  path: string;
  label: string;
  submenus: NavItem[];
}

type User = (AccountWithUserType & AccountWithType & { subscriptionType?: string }) | null | undefined;

/**
 * Type guard to check if navItem has submenus
 */
function hasSubmenus(navItem: NavItem | NavItemWithSubmenu): navItem is NavItemWithSubmenu {
  return 'submenus' in navItem && Array.isArray(navItem.submenus);
}

/**
 * Custom hook that returns the Account menu navigation items filtered by feature flags
 * and subscription types. Dynamically filters submenu items based on FEATURE_FLAG_NAV_CONFIG.
 *
 * @param user - The current user object from AppContext
 * @param navMenu - The full navigation menu array to filter (e.g. AccountNav)
 * @returns Filtered array of navigation items with feature-flagged items removed when disabled
 *          or when user's subscription type doesn't match allowed types
 */
export function useFilteredNav(user: User, navMenu: NavMenuItemType[]): NavArray {
  const flags = useFeatureFlags();

  return useMemo(() => {
    if (navMenu.length === 0) {
      return navMenu;
    }

    const userSubscriptionType = user?.subscriptionType;

    // Collect all nav paths that should be hidden based on feature flags and subscription types
    const hiddenNavPaths = new Map<string, Set<string>>();

    for (const [flagName, config] of Object.entries(FEATURE_FLAG_NAV_CONFIG)) {
      const flagDisabled = !flags[flagName as keyof FeatureFlags];
      if (!config) continue;
      const subscriptionMismatch = config.subscriptionTypes &&
        (!userSubscriptionType || !config.subscriptionTypes.includes(userSubscriptionType));

      // Hide if flag is disabled OR if subscription type doesn't match (when specified)
      if (flagDisabled || subscriptionMismatch) {
        if (!hiddenNavPaths.has(config.parentPath)) {
          hiddenNavPaths.set(config.parentPath, new Set());
        }
        hiddenNavPaths.get(config.parentPath)!.add(config.navPath);
      }
    }

    // No filtering needed if nothing to hide
    if (hiddenNavPaths.size === 0) {
      return navMenu;
    }

    // Filter out hidden nav items from submenus
    return navMenu.map((navItem) => {
      const pathsToHide = hiddenNavPaths.get(navItem.path);

      if (pathsToHide && hasSubmenus(navItem)) {
        return {
          ...navItem,
          submenus: navItem.submenus.filter(
            (submenu: NavItem) => !pathsToHide.has(submenu.path)
          )
        };
      }
      return navItem;
    });
  }, [user, navMenu, flags]);
}
